import { Component, EventEmitter, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import Contact from '../../../model/Contact/contact';
import { Email } from '../../../model/email';
import { Telephone } from '../../../model/telephone';
import { merge, of as observableOf, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NOT_FOUND, RequestService } from '../../../services/request.service';
import { FormatPhoneNumber } from '../../../modules/app-pipes/format-phonenumber';
import Company from '../../../model/company/company';
import { SnackService } from '../../../services/snack.service';
import { CompanyContactsDialogComponent } from 'src/app/modals/company-contacts-dialog/company-contacts-dialog.component';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { AuthService } from '../../../services/auth.service';
import UserHelper from '../../../helpers/user-helper';
import { User } from '../../../model/user';
import { Role } from '../../../model/role';
import { RoleName } from '../../../model/role-name';
import { ModalService } from 'src/app/services/modal.service';

@Component({
  selector: 'edit-contact',
  templateUrl: './edit-contact.component.html',
  styleUrls: ['./edit-contact.component.scss'],
  providers: [FormatPhoneNumber],
})
export class EditContactComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  public contactName = '';
  public isInitialised = false;
  public isLoadingObject = false;
  public contactForm: UntypedFormGroup;
  private reloadData: EventEmitter<any>;
  private contact: Contact;
  private email: Email;
  private telephone: Telephone;
  private mobile: Telephone;
  private contactUser: User;
  public salutations = Salutation;
  public types = ContactType;
  companies: Company[];
  private dialogRef: MatDialogRef<CompanyContactsDialogComponent>;
  isAdminLoggedIn: boolean = false;
  contactHasLogin: boolean = false;

  displayedColumns: string[] = ['name', 'phone', 'email'];

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private requestService: RequestService,
    private route: ActivatedRoute,
    public formatPhoneNumber: FormatPhoneNumber,
    private snackService: SnackService,
    public dialog: MatDialog,
    private authService: AuthService,
    private modalService: ModalService
  ) {
    super();
  }

  ngOnInit() {
    this.initIsAdmin();
    this.contactForm = this.fb.group({});
    this.reloadData = new EventEmitter<any>();
    this.initObject();
  }

  private initIsAdmin() {
    this.addSubscription(
      this.authService.getCurrentUser(),
      (res) => {
        if (res != null && res.body != null) {
          const user = res.body;
          this.isAdminLoggedIn = UserHelper.isAdmin(user);
        } else {
          this.authService.logout();
          this.router.navigateByUrl('/login');
        }
      },
      (err) => {
        if (err.status === 401) {
          this.authService.logout();
          this.router.navigateByUrl('/login');
        }
      }
    );
  }

  private buildForm() {
    this.contactForm = this.fb.group({
      firstName: [this.contact.firstName, [Validators.required]],
      lastName: [this.contact.lastName, [Validators.required]],
      telephone: [this.telephone.number],
      mobile: [this.mobile.number],
      email: [this.email.email],
      personalInfo: [this.contact.personalInfo],
      gender: [this.contact.gender],
      birthdate: [this.contact.birthdate],
      type: [this.contact.type],
    });
    this.isInitialised = true;
    this.initCompanies();
  }

  private initCompanies() {
    if (this.contact.id != null) {
      this.requestService
        .get(
          '/contacts/getcompanies/' + this.contact.id,
          null,
          null,
          null,
          null,
          null,
          null
        )
        .subscribe((result) => {
          this.contact.companies = result.body;
          this.companies = result.body.filter((c) => !c.archived);
        });
    }
  }

  private initObject(): void {
    merge(this.route.params, this.reloadData)
      .pipe(
        switchMap((params) => {
          this.isLoadingObject = true;
          if (params['id'] != null) {
            return this.requestService.get(
              '/contacts/' + params['id'],
              null,
              null,
              null,
              null,
              null,
              null
            );
          } else {
            let contact = new Contact();
            let email = new Email();
            email.type = 'primary';
            contact.emails = [email];
            let telephone = new Telephone();
            telephone.type = 'phone';
            let mobile = new Telephone();
            telephone.type = 'mobile';
            contact.telephones = [telephone, mobile];
            return observableOf({ body: contact });
          }
        }),
        map((e: any) => {
          // Flip flag to show that loading has finished.
          this.isLoadingObject = false;
          return e;
        }),
        catchError((e) => {
          this.isLoadingObject = false;
          this.router.navigateByUrl('/contacten');

          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.contact = data.body as Contact;
        this.contactName = this.contact.firstName + ' ' + this.contact.lastName;

        this.initEmails();
        this.initTelephones();
        this.initDate();
        if (this.contact != null) {
          this.buildForm();
        } else {
          this.contact = new Contact();
          this.buildForm();
        }
        this.initContactHasLogin();
      });
  }

  initDate() {
    if (this.contact.birthdate != null) {
      if (this.contact.birthdate.length < 11) {
        this.contact.birthdate += 'T20:02:02.022Z';
      }
    }
  }

  initEmails() {
    if (this.contact.emails != null) {
      this.contact.emails.forEach((e) => {
        if (e.type != null && e.type === 'primary') {
          this.email = e;
        }
      });
    }
    if (this.email == null) {
      this.email = new Email();
      this.email.type = 'primary';
      this.contact.emails.push(this.email);
    }
  }

  initTelephones() {
    if (this.contact.telephones != null) {
      this.contact.telephones.forEach((t) => {
        if (t.type != null && t.type === 'phone') {
          this.telephone = t;
        }
        if (t.type != null && t.type === 'mobile') {
          this.mobile = t;
        }
      });
    }
    if (this.telephone == null) {
      this.telephone = new Telephone();
      this.telephone.type = 'phone';
      this.telephone.number = '';
      this.contact.telephones.push(this.telephone);
    }
    if (this.mobile == null) {
      this.mobile = new Telephone();
      this.mobile.type = 'mobile';
      this.mobile.number = '';
      this.contact.telephones.push(this.mobile);
    }
  }

  isNotEmptyOrNull(value) {
    return value !== '' && value != null;
  }

  save() {
    if (this.contactForm.valid) {
      this.setFormValues();
      this.contact.emails = this.contact.emails.filter((email) =>
        this.isNotEmptyOrNull(email.email)
      );

      this.contact.telephones = this.contact.telephones.filter((phone) =>
        this.isNotEmptyOrNull(phone.number)
      );

      if (this.contact.id === 0 || !this.contact.id) {
        this.requestService.post(this.contact, '/contacts').subscribe(
          (res) => {
            if (res) {
              if (res.success == false) {
                this.snackService.showError(res.message);
              } else {
                this.router.navigateByUrl('/contacten');
              }
            }
          },
          (err) => {
            this.handleSaveError(err);
          }
        );
      } else {
        this.requestService
          .put(this.contact, '/contacts/updatecontact')
          .subscribe(
            (res) => {
              if (res) {
                if (res.success == false) {
                  this.snackService.showError(res.message);
                } else {
                  this.snackService.showSuccess('Contact succesvol aangepast');
                  this.router.navigateByUrl('/contacten');
                }
              }
            },
            (err) => {
              this.handleSaveError(err);
            }
          );
      }
    } else {
      this.validateAllFields(this.contactForm);
    }
  }

  setFormValues() {
    this.contact.firstName = this.contactForm.get('firstName').value;
    this.contact.lastName = this.contactForm.get('lastName').value;
    this.contact.gender = this.contactForm.get('gender').value;
    this.contact.birthdate = this.contactForm.get('birthdate').value;
    this.email.email = this.contactForm.get('email').value;
    this.contact.personalInfo = this.contactForm.get('personalInfo').value;
    this.contact.type = this.contactForm.get('type').value;
    if (this.contactForm.get('mobile').value != null) {
      this.mobile.number = this.formatPhoneNumber.transform(
        this.contactForm.get('mobile').value
      );
    } else {
      this.mobile.number = '';
    }
    if (this.contactForm.get('telephone').value != null) {
      this.telephone.number = this.formatPhoneNumber.transform(
        this.contactForm.get('telephone').value
      );
    } else {
      this.telephone.number = '';
    }
  }

  validateAllFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        if (control.validator) {
          const validator = control.validator({} as AbstractControl);
          if (
            validator &&
            (validator.required || validator.validateVat) &&
            control.value !== ''
          ) {
            control.markAsTouched({ onlySelf: true });
          }
        }
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFields(control);
      }
    });
  }

  private handleSaveError(error) {
    let errMsg =
      'Sorry, er is iets misgegaan, probeer later opnieuw. (Statuscode: ' +
      error.status +
      ')';
    if (error.error) {
      switch (error.error.message) {
        case 'invalid_owner':
          errMsg = 'Je hebt geen rechten om dit object te bewerken';
          break;
      }
    }

    this.snackService.showError(errMsg);
    return throwError(null);
  }

  openCompanyContactDialog() {
    let config = {
      contact: this.contact,
    };
    this.dialogRef = this.dialog.open(CompanyContactsDialogComponent, {
      data: config,
    });
    this.addSubscription(this.dialogRef.afterClosed(), () => {
      this.companies =
        this.contact.companies != null
          ? this.contact.companies.filter((c) => !c.archived)
          : [];
    });
  }

  createClientCornerUser() {
    const user = new User();
    user.firstName = this.contact.firstName;
    user.lastName = this.contact.lastName;
    user.email = this.email.email;
    user.phone = this.telephone.number;
    user.roles = [new Role(RoleName.ROLE_PORTAL_USER)];
    this.requestService.post(user, '/users').subscribe(
      (res) => {
        if (res) {
          if (res.success == false) {
            this.snackService.showError(res.message);
          } else {
            this.contactUser = res;
            this.snackService.showSuccess('Gebruiker succesvol toegevoegd');
          }
        }
      },
      (err) => {
        this.handleSaveError(err);
      }
    );
  }

  navigateToContactUser() {
    this.router.navigate(['/users/edit', this.contactUser.id], {
      queryParams: {
        returnRoute:
          this.contact.id == null ? '/users' : '/contacten/' + this.contact.id,
      },
    });
  }

  initContactHasLogin(): void {
    if (this.email && this.email.email) {
      this.requestService
        .get(
          `/users/email/${this.email.email}`,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          false,
          false
        )
        .toPromise()
        .then((data) => {
          this.contactHasLogin = true;
          this.contactUser = data.body;
        })
        .catch((err) => {
          console.error(err);
          this.contactHasLogin = false;

          if (err != NOT_FOUND)
            this.snackService.showError(
              'Er is iets misgegaan, probeer later opnieuw.'
            );
        });
    } else {
      this.contactHasLogin = false;
    }
  }

  resendLoginEmail() {
    this.modalService.confirmDialog(
      'Login mail opnieuw versturen',
      'Bent u zeker dat u opnieuw een login mail wil versturen naar deze gebruiker?',
      (b) => {
        if (b) {
          this.addSubscription(
            this.requestService.post(
              {
                email: this.email.email,
                resend: true,
              },
              '/auth/recoverAccount'
            ),
            () => {
              this.snackService.showSuccess('Login mail opnieuw verstuurd');
            }
          );
        }
      }
    );
  }
}

export const Salutation = [
  {
    value: 'male',
    label: 'Mijnheer',
  },
  {
    value: 'female',
    label: 'Mevrouw',
  },
];

export const ContactType = [
  {
    value: 'BUSINESS_MANAGER',
    label: 'Bestuurder',
  },
  {
    value: 'OFFICE_MANAGER',
    label: 'Office manager',
  },
  {
    value: 'MARKETING_MANAGER',
    label: 'Marketing verantwoordelijke',
  },
  {
    value: 'REAL_ESTATE_CONSULTANT',
    label: 'Vastgoedconsulent',
  },
  {
    value: 'EMPLOYEE',
    label: 'Administratief medewerker',
  },
];
